/**
 * Google 認証（社員のログイン）の設定（ADR-049・AUTHZ-2TIER-REBUILD-1）。
 *
 * - 社のドメインのアカウントだけ: `hd` は Google への選択ヒントであると同時に、ID トークンの `hd` claim を
 *   better-auth がサーバーで検証する（v1.7.5 docs/content/docs/authentication/google.mdx 151行
 *   「Better Auth enforces the returned ID token/profile claim after Google signs the response」）
 * - 既存の社員だけ: `disableSignUp: true` で新しいユーザーを作らない（reference/options.mdx 348行）。
 *   ID トークン経路はこの値が効かないので `disableIdTokenSignIn: true` で閉じる（同 357行）。
 *   既存ユーザーとはメールが同じなら account が足される（concepts/users-accounts.mdx 493行・auth.ts の accountLinking）。
 *   ただし手元の users.email_verified が true の人だけ（better-auth の requireLocalEmailVerified の既定）。鍵を入れる前に数える
 * - 明示の有効化（`GOOGLE_SIGN_IN_ENABLED=true`）と鍵2つが揃ったときだけ有効。どちらか欠ければ provider を作らず、
 *   ログイン画面にもボタンを出さない。鍵があるだけでは有効にしない: `GOOGLE_CLIENT_ID` は昔 Gmail API 用として
 *   文書にあった名前で、鍵が残っている環境でマージした瞬間に、鍵投入前の決め事（ADR-049 決定8）より先に開くため
 *
 * 退職者・無効化の遮断は、ログイン方法によらず session 作成の直前（auth.ts の databaseHooks.session.create.before）で掛かる。
 */

import { buildAfterLoginHref, buildLoginHref } from '@/lib/utils/safe-next-path'

export const GOOGLE_HOSTED_DOMAIN = 'g-house.osaka.jp'

type GoogleEnv = {
  GOOGLE_SIGN_IN_ENABLED?: string
  GOOGLE_CLIENT_ID?: string
  GOOGLE_CLIENT_SECRET?: string
}

const keysOf = (env: GoogleEnv) => {
  if (env.GOOGLE_SIGN_IN_ENABLED !== 'true') return null
  const clientId = env.GOOGLE_CLIENT_ID?.trim() ?? ''
  const clientSecret = env.GOOGLE_CLIENT_SECRET?.trim() ?? ''
  return clientId && clientSecret ? { clientId, clientSecret } : null
}

/** 有効化され、鍵が2つとも入っているか（ログイン画面にボタンを出すか） */
export function isGoogleSignInConfigured(env: GoogleEnv): boolean {
  return keysOf(env) !== null
}

/** better-auth の `socialProviders` に渡す。有効化か鍵が欠ければ空 */
export function buildGoogleSocialProviders(env: GoogleEnv) {
  const keys = keysOf(env)
  if (!keys) return {}
  return {
    google: {
      ...keys,
      hd: GOOGLE_HOSTED_DOMAIN,
      disableSignUp: true,
      // ログイン画面はリダイレクト経路しか使わない。ID トークン経路は provider.disableSignUp を見るが、better-auth は
      // そこへ disableImplicitSignUp しか写さないので、開けたままだと新しい users 行が作られる（reference/options.mdx 357行）
      disableIdTokenSignIn: true,
    },
  }
}

/**
 * ログイン画面から始める Google サインインの依頼（`authClient.signIn.social` に渡す）。
 * 通ったらパスワードのログインと同じ行き先を決める口へ（AUTH-01・必須の人は登録の画面へ）。
 * 失敗したらログイン画面へ戻す（公式 errorCallbackURL・既定のエラー画面へ出さない）
 */
export function buildGoogleSignInRequest(nextPath: string) {
  return {
    provider: 'google' as const,
    callbackURL: buildAfterLoginHref(nextPath),
    errorCallbackURL: buildLoginHref(nextPath),
  }
}
